import { useEffect } from 'react';
import { Accordion, Heading, IconSearch, Paragraph, Spacer, Text } from '@zillow/constellation';
import type { BackgroundReport } from '@zg-rentals/ts-capps-client';
import { BackgroundReportAlert, getBackgroundReportAlerts, getFirstReportAlert } from './BackgroundReportAlerts';
import { pluralize } from '@zg-rentals/applications-utils';
import { getCurrencyDisplay, getTitleCase } from '../../../utils/formatters';
import { EvictionSectionContent } from './EvictionSectionContent';
import { type EmitMetric, ReportType, UserType } from '../../../models/report-models';
import { countReportData } from '../../../utils/report-utils';

export function BackgroundReportEvictions({
  userType,
  backgroundReport,
  emitMetric,
}: {
  userType: UserType;
  backgroundReport: BackgroundReport;
  emitMetric: EmitMetric;
}) {
  const evictionRecordsCount = backgroundReport?.evictionReport?.records?.length || 0;

  useEffect(() => {
    countReportData(ReportType.BackgroundReport, 'evictions', evictionRecordsCount, emitMetric);
  }, []);

  const alert = getFirstReportAlert(getBackgroundReportAlerts(userType, backgroundReport), ['evictionReport'])?.summary;

  return (
    <>
      <Heading level={5} as="h3" marginY="md">
        Housing court record
      </Heading>
      <Spacer marginBottom="md">
        <BackgroundReportAlert
          userType={userType}
          backgroundReport={backgroundReport}
          onlySources={['evictionReport']}
        />
      </Spacer>

      <Paragraph>
        <IconSearch marginRight="xs" />
        {alert ||
          (evictionRecordsCount > 0 ? (
            <>
              Total <strong>{pluralize('housing court record', evictionRecordsCount)}</strong> found.
            </>
          ) : (
            'No housing court records found.'
          ))}
      </Paragraph>

      <Spacer marginY="xs">
        <Text fontType="finePrint">
          The result of housing court record searches may vary according to (1) collection restrictions by the different
          court jurisdictions which may result in some areas not having coverage and (2) reporting restrictions due to
          government regulations applicable to the rental property’s location. CIC applies industry best practices in
          the attempt to accurately match and report these records.
        </Text>
      </Spacer>
      {evictionRecordsCount > 0 && (
        <Accordion>
          {backgroundReport?.evictionReport?.records?.map((record, index) => (
            <Accordion.Item key={index}>
              <Accordion.Header>
                <Heading level={5} as="h4" fontType="body">
                  {getTitleCase(record.court)}
                </Heading>
                {record.judgementAmount && <Text fontType="h6">{getCurrencyDisplay(record.judgementAmount)}</Text>}
              </Accordion.Header>
              <Accordion.Panel>
                <EvictionSectionContent record={record} />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </>
  );
}
